import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { AUTH_COLORS, AUTH_FONT } from "../../config/authConstants";
import unsavedIllustration from "../../Assests/Images/joinee/unsaved-changes.png";
import iconCloseCircle from "../../Assests/Images/joinee/icon-close-circle.png";

/*
 * Dirty state lives outside React so the API layer can clear it synchronously
 * on a successful save, before the calling form navigates away.
 */
var store = { dirty: false, listeners: [] };

function emit() {
  store.listeners.slice().forEach(function (fn) {
    fn(store.dirty);
  });
}

export function markFormDirty() {
  if (store.dirty) return;
  store.dirty = true;
  emit();
}

export function markFormClean() {
  if (!store.dirty) return;
  store.dirty = false;
  emit();
}

export function isFormDirty() {
  return store.dirty;
}

function subscribe(fn) {
  store.listeners.push(fn);
  return function () {
    store.listeners = store.listeners.filter(function (l) {
      return l !== fn;
    });
  };
}

/** MUI renders Select menus / date pickers in portals, so match their pick targets too. */
var PORTAL_PICK_SELECTOR = [
  '[role="option"]',
  ".MuiPickersDay-root",
  ".MuiPickersYear-yearButton",
  ".MuiPickersMonth-monthButton",
  ".MuiChip-deleteIcon",
].join(",");

var ROW_REMOVE_ICON_SELECTOR = '[data-testid^="Delete"], [data-testid^="RemoveCircle"]';

var UnsavedChangesContext = createContext(null);

var DEFAULT_MESSAGE = "Leaving this page will discard them.";

export function UnsavedChangesProvider(props) {
  var [trackers, setTrackers] = useState(0);
  var [dialog, setDialog] = useState(null);
  var [saving, setSaving] = useState(false);
  var saverRef = useRef(null);
  var sentinelRef = useRef(false);
  var pendingLeaveRef = useRef(null);
  var tracking = trackers > 0;

  /** Drops the back-button sentinel history entry (if any) before leaving. */
  function leave(proceed) {
    if (sentinelRef.current) {
      sentinelRef.current = false;
      pendingLeaveRef.current = proceed;
      window.history.back();
      return;
    }
    proceed();
  }

  var startTracking = useCallback(function () {
    markFormClean();
    setTrackers(function (n) {
      return n + 1;
    });
    return function () {
      markFormClean();
      sentinelRef.current = false;
      setTrackers(function (n) {
        return Math.max(0, n - 1);
      });
    };
  }, []);

  var registerSaver = useCallback(function (fn) {
    saverRef.current = fn;
    return function () {
      if (saverRef.current === fn) saverRef.current = null;
    };
  }, []);

  var guard = useCallback(
    function (proceed, message) {
      if (!tracking || !isFormDirty()) {
        proceed();
        return;
      }
      setDialog({ proceed: proceed, message: message || DEFAULT_MESSAGE });
    },
    [tracking]
  );

  useEffect(
    function () {
      if (!tracking) return undefined;

      function onEdit(event) {
        var target = event.target;
        if (!target || (target.closest && target.closest("[data-unsaved-ignore]"))) {
          return;
        }
        markFormDirty();
      }

      function onPick(event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (target.closest(PORTAL_PICK_SELECTOR)) {
          markFormDirty();
          return;
        }
        var button = target.closest("button");
        if (button && button.querySelector(ROW_REMOVE_ICON_SELECTOR)) {
          markFormDirty();
        }
      }

      function onBeforeUnload(event) {
        if (!isFormDirty()) return undefined;
        event.preventDefault();
        event.returnValue = "";
        return "";
      }

      function onPopState() {
        if (pendingLeaveRef.current) {
          var pending = pendingLeaveRef.current;
          pendingLeaveRef.current = null;
          pending();
          return;
        }
        if (!sentinelRef.current) return;
        sentinelRef.current = false;
        if (!isFormDirty()) {
          window.history.back();
          return;
        }
        setDialog({
          message: DEFAULT_MESSAGE,
          proceed: function () {
            window.history.back();
          },
          onStay: function () {
            window.history.pushState(window.history.state, "", window.location.href);
            sentinelRef.current = true;
          },
        });
      }

      var unsubscribe = subscribe(function (dirty) {
        if (dirty && !sentinelRef.current) {
          window.history.pushState(window.history.state, "", window.location.href);
          sentinelRef.current = true;
        }
      });

      document.addEventListener("input", onEdit, true);
      document.addEventListener("change", onEdit, true);
      document.addEventListener("click", onPick, true);
      window.addEventListener("beforeunload", onBeforeUnload);
      window.addEventListener("popstate", onPopState);
      return function () {
        unsubscribe();
        document.removeEventListener("input", onEdit, true);
        document.removeEventListener("change", onEdit, true);
        document.removeEventListener("click", onPick, true);
        window.removeEventListener("beforeunload", onBeforeUnload);
        window.removeEventListener("popstate", onPopState);
      };
    },
    [tracking]
  );

  function stay() {
    if (saving) return;
    var current = dialog;
    setDialog(null);
    if (current && current.onStay) current.onStay();
  }

  function discard() {
    var current = dialog;
    setDialog(null);
    markFormClean();
    if (current) leave(current.proceed);
  }

  function saveAndContinue() {
    var current = dialog;
    var saver = saverRef.current;
    if (!saver) {
      discard();
      return;
    }
    setSaving(true);
    Promise.resolve()
      .then(function () {
        return saver();
      })
      .catch(function () {
        return null;
      })
      .then(function () {
        setSaving(false);
        setDialog(null);
        if (!isFormDirty()) {
          if (current) leave(current.proceed);
        } else if (current && current.onStay) {
          current.onStay();
        }
      });
  }

  var value = { guard: guard, startTracking: startTracking, registerSaver: registerSaver };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {props.children}
      <UnsavedChangesDialog
        open={Boolean(dialog)}
        message={dialog ? dialog.message : DEFAULT_MESSAGE}
        saving={saving}
        canSave={Boolean(saverRef.current)}
        onClose={stay}
        onDiscard={discard}
        onSave={saveAndContinue}
      />
    </UnsavedChangesContext.Provider>
  );
}

/** Returns guard(proceed, message): runs `proceed` now, or after the user confirms leaving. */
export function useUnsavedGuard() {
  var ctx = useContext(UnsavedChangesContext);
  return ctx
    ? ctx.guard
    : function (proceed) {
        proceed();
      };
}

/** Enables edit tracking while the calling form is mounted and `enabled` is true. */
export function useUnsavedTracking(enabled) {
  var ctx = useContext(UnsavedChangesContext);
  var startTracking = ctx ? ctx.startTracking : null;
  useEffect(
    function () {
      if (!enabled || !startTracking) return undefined;
      return startTracking();
    },
    [enabled, startTracking]
  );
}

/** Lets "Save and Continue" in the dialog reuse the form's own Save as Draft action. */
export function useUnsavedSaver(saver) {
  var ctx = useContext(UnsavedChangesContext);
  var registerSaver = ctx ? ctx.registerSaver : null;
  var saverRef = useRef(saver);
  saverRef.current = saver;
  useEffect(
    function () {
      if (!registerSaver || !saverRef.current) return undefined;
      return registerSaver(function () {
        return saverRef.current ? saverRef.current() : null;
      });
    },
    [registerSaver, Boolean(saver)]
  );
}

function UnsavedChangesDialog(props) {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={false}
      PaperProps={{
        "data-unsaved-ignore": true,
        sx: {
          width: 510,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "16px",
          p: { xs: 2.5, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          position: "relative",
          textAlign: "center",
          fontFamily: AUTH_FONT,
        },
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={props.onClose}
        disabled={props.saving}
        sx={{ position: "absolute", top: 16, right: 16, p: 0 }}
      >
        <Box component="img" src={iconCloseCircle} alt="" sx={{ width: 40, height: 40 }} />
      </IconButton>

      <Box
        component="img"
        src={unsavedIllustration}
        alt=""
        sx={{ width: 272, maxWidth: "70%", height: "auto", mx: "auto", mt: 3, display: "block" }}
      />

      <Typography
        sx={{
          fontFamily: AUTH_FONT,
          fontSize: { xs: 19, sm: 22 },
          fontWeight: 600,
          color: AUTH_COLORS.text,
          mt: 3,
        }}
      >
        You have unsaved changes
      </Typography>
      <Typography
        sx={{
          fontFamily: AUTH_FONT,
          fontSize: { xs: 14, sm: 16 },
          color: AUTH_COLORS.textSecondary,
          lineHeight: 1.6,
          mt: 1,
        }}
      >
        Your changes haven&apos;t been saved yet.
        <br />
        {props.message}
      </Typography>
      <Typography
        sx={{
          fontFamily: AUTH_FONT,
          fontSize: { xs: 14, sm: 16 },
          color: AUTH_COLORS.primary,
          mt: 0.5,
        }}
      >
        Do you want to continue?
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mt: 3.5,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={props.onDiscard}
          disabled={props.saving}
          sx={{
            fontFamily: AUTH_FONT,
            fontSize: 16,
            fontWeight: 400,
            textTransform: "none",
            height: 48,
            borderRadius: "6px",
            color: AUTH_COLORS.error,
            borderColor: AUTH_COLORS.border,
            bgcolor: "#FFFFFF",
            "&:hover": { borderColor: AUTH_COLORS.error, bgcolor: AUTH_COLORS.errorBg },
          }}
        >
          Discard changes
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={props.canSave ? props.onSave : props.onClose}
          disabled={props.saving}
          startIcon={props.saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            fontFamily: AUTH_FONT,
            fontSize: 16,
            fontWeight: 400,
            textTransform: "none",
            height: 48,
            borderRadius: "6px",
            bgcolor: AUTH_COLORS.primary,
            color: AUTH_COLORS.buttonLabel,
            boxShadow: "none",
            "&:hover": { bgcolor: AUTH_COLORS.primaryHover, boxShadow: "none" },
          }}
        >
          {props.saving ? "Saving..." : props.canSave ? "Save and Continue" : "Stay on page"}
        </Button>
      </Box>
    </Dialog>
  );
}
